import React from 'react';
import propTypes from 'prop-types';
import { cn } from 'lib/utils';
import style from './CollapsibleList.module.scss';
import { ChevronDown, MoreVertical } from 'lucide-react';
import DropDownMenu from 'components/DropDownMenu/DropDownMenu';
import DropDownMenuItem from 'components/DropDownMenu/DropDownMenuItem/DropDownMenuItem';
import Button from 'tailwind-components/Button/Button';
import { Link } from 'react-router-dom';

const TITLE_COLOR = {
  primary: style.titlePrimary,
  secondary: style.titleSecondary
};

const CollapsibleSection = ({
  id,
  title,
  titleColor = 'primary',
  titleAlert = null,
  titleTrailing = null,
  options,
  optionsArray,
  subtitle,
  arrowRight = false,
  expanded = false,
  uncontrolled = false,
  highlightFirst = false,
  children,
  onClick = () => {},
  listItemProps = {},
  titleAnchorProps = {}
}) => {
  const { className: listItemClassName, ...restListItemProps } = listItemProps;
  return (
    <li
      className={cn(style.section, style.mainSection, listItemClassName)}
      {...restListItemProps}
    >
      {uncontrolled ? (
        <input type='checkbox' id={id} defaultChecked={!!expanded} />
      ) : (
        <input
          type='checkbox'
          checked={!!expanded}
          readOnly
          id={id}
          onClick={onClick}
        />
      )}

      <label htmlFor={id}>
        <div className={`${arrowRight ? style.arrowRight : ''}`}>
          {!arrowRight && (
            <div className={style.arrow} title='Expand'>
              <ChevronDown size={24} />
            </div>
          )}
          <div className={style.texts}>
            {titleAlert ? (
              <div className={style.titleContainer}>
                <span
                  className={`${style.title} ${TITLE_COLOR[titleColor]} ${
                    highlightFirst ? style.firstParent : null
                  }`}
                  {...titleAnchorProps}
                >
                  {title}
                </span>
                {titleAlert}
              </div>
            ) : (
              <span
                className={`${style.title} ${TITLE_COLOR[titleColor]} ${
                  highlightFirst ? style.firstParent : null
                }`}
                {...titleAnchorProps}
              >
                {title}
              </span>
            )}
            {subtitle && <span className={style.subtitle}>{subtitle}</span>}
          </div>
        </div>
        {(options || optionsArray) && (
          <div className={style.options}>
            <div className={style.optionsLg}>
              {optionsArray
                ? optionsArray.map((option, index) => (
                    <Link
                      key={'option_' + index}
                      to={option.to}
                      onClick={option.onClick}
                    >
                      <Button
                        variant='transparent'
                        size='xsmall'
                        rounded={'large'}
                        text={option.text}
                        outlined
                        icon={option.icon}
                      />
                    </Link>
                  ))
                : options}
            </div>
            {optionsArray && (
              <div className={style.optionsSm}>
                <DropDownMenu
                  title='Options'
                  menuTrigger={
                    <div title='Options'>
                      <Button
                        size='medium'
                        variant='transparent'
                        outlined
                        icon={<MoreVertical size={24} />}
                      />
                    </div>
                  }
                >
                  {optionsArray.map((option, index) => (
                    <DropDownMenuItem
                      key={'option_' + index}
                      text={option.text}
                      icon={option.icon}
                      to={option.to}
                      onClick={option.onClick}
                    />
                  ))}
                </DropDownMenu>
              </div>
            )}
          </div>
        )}
        {(titleTrailing || arrowRight) && (
          <div className={style.titleEnd}>
            {titleTrailing ? (
              <div className={style.titleTrailing}>{titleTrailing}</div>
            ) : null}
            {arrowRight && (
              <div className={style.arrow} title='Expand'>
                <ChevronDown size={24} />
              </div>
            )}
          </div>
        )}
      </label>
      <ul className={arrowRight ? style.arrowRight : ''}>{children}</ul>
    </li>
  );
};

CollapsibleSection.propTypes = {
  id: propTypes.string,
  title: propTypes.string,
  titleColor: propTypes.string,
  options: propTypes.element,
  optionsArray: propTypes.array,
  subtitle: propTypes.string,
  arrowRight: propTypes.bool,
  expanded: propTypes.bool,
  uncontrolled: propTypes.bool,
  highlightFirst: propTypes.bool | undefined,
  children: propTypes.node,
  onClick: propTypes.func,
  titleAlert: propTypes.element,
  listItemProps: propTypes.object,
  titleAnchorProps: propTypes.object,
  titleTrailing: propTypes.node
};

export default CollapsibleSection;
